import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import DashboardPagination from '../components/DashboardPagination';
import Toast from '../components/Toast';
import PreProjetoFiltros from '../components/PreProjetoFiltros';
import { filtrosVazios, passaNosFiltros, type FiltrosPreProjeto } from '../utils/preProjetoFiltros';
import { useAuth } from '../context/AuthContext';
import { Permission } from '../types/user.types';
import parametroSistemaService from '../services/parametroSistemaService';
import preProjetoService, { type PreProjetoDTO, type PreProjetoEtapa } from '../services/preProjetoService';
import { UI_THEME } from '../ui/theme';
import { FIELD_BASE_CLASS, FIELD_CLASS } from '../components/ui/fieldStyles';
import Button from '../components/ui/Button';
import { Table, Tbody, Td, Th, Thead, Tr } from '../components/ui/Table';

const money = (value: number) => new Intl.NumberFormat('pt-BR', {
  style: 'currency', currency: 'BRL',
}).format(value || 0);

const ETAPA_LABEL: Record<PreProjetoEtapa, string> = {
  RASCUNHO: 'Rascunho',
  PUBLICADO: 'Publicado',
  SELECIONADO: 'Selecionado',
  ARQUIVADO: 'Arquivado',
};

const PreProjetosPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, hasPermission } = useAuth();
  const isSaas = UI_THEME === 'saas';
  // A permissão configurada decide; o papel só garante que o usuário tem instituição para lançar.
  const canManage = hasPermission(Permission.MANAGE_PRE_PROJETOS)
    && ['ADMIN', 'OPERADOR'].includes(String(user?.role || '').toUpperCase());
  const [items, setItems] = useState<PreProjetoDTO[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [etapa, setEtapa] = useState('');
  const [somenteBloqueadas, setSomenteBloqueadas] = useState(false);
  const [filtros, setFiltros] = useState<FiltrosPreProjeto>(filtrosVazios);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [bloqueioJanela, setBloqueioJanela] = useState<string | null>(null);
  const [toastMessage, setToastMessage] = useState((location.state as { message?: string } | null)?.message || '');

  useEffect(() => {
    preProjetoService.list()
      .then(setItems)
      .catch((e) => setError(e?.response?.data?.error || 'Não foi possível carregar os pré-projetos.'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!canManage) return;
    parametroSistemaService.getParametros()
      // Durante a retificação também é possível cadastrar e publicar projetos novos.
      .then((p) => setBloqueioJanela(p.preProjetoRetificacaoAberta ? null : p.preProjetoBloqueioMensagem))
      .catch(() => setBloqueioJanela(null));
  }, [canManage]);

  useEffect(() => {
    if (toastMessage) navigate(location.pathname, { replace: true, state: null });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filtered = useMemo(() => items.filter((item) => {
    const term = search.trim().toLowerCase();
    const matchesSearch = !term || [item.codigo, item.titulo, item.objeto, item.instituicaoNome, item.areaAtuacao]
      .some((value) => value?.toLowerCase().includes(term));
    return matchesSearch && passaNosFiltros(item, filtros) && (!etapa || item.etapa === etapa) && (!somenteBloqueadas || item.instituicaoBloqueada);
  }), [items, search, etapa, somenteBloqueadas, filtros]);

  const visible = filtered.slice((page - 1) * pageSize, page * pageSize);
  const totals = useMemo(() => ({
    rascunhos: items.filter((i) => i.etapa === 'RASCUNHO').length,
    publicados: items.filter((i) => i.etapa === 'PUBLICADO').length,
    selecionados: items.filter((i) => i.etapa === 'SELECIONADO').length,
  }), [items]);

  const summaryCards = [
    { label: 'Rascunhos', value: totals.rascunhos, dot: 'bg-slate-400' },
    { label: 'Publicados', value: totals.publicados, dot: 'bg-blue-500' },
    { label: 'Selecionados', value: totals.selecionados, dot: 'bg-emerald-500' },
  ];

  return (
    <div className="w-full space-y-6">
      {toastMessage && <Toast message={toastMessage} type="success" onClose={() => setToastMessage('')} />}
      <section className={`rounded-2xl border p-6 shadow-sm ${
        isSaas
          ? 'border-teal-100 bg-gradient-to-br from-white via-teal-50/50 to-indigo-50 dark:border-slate-700 dark:from-slate-900 dark:via-slate-900 dark:to-slate-800'
          : 'border-slate-200 bg-gradient-to-br from-white via-slate-50 to-sky-50 dark:border-slate-700 dark:from-slate-900 dark:via-slate-900 dark:to-slate-800'
      }`}>
        <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div className="space-y-2">
            <p className={`text-xs font-semibold uppercase tracking-[0.2em] ${isSaas ? 'text-teal-700 dark:text-teal-300' : 'text-sky-700 dark:text-sky-400'}`}>
              {canManage ? 'Banco de propostas' : 'Catálogo de propostas'}
            </p>
            <div>
              <h1 className="text-3xl font-bold tracking-tight text-slate-950 dark:text-slate-100">Pré-projetos</h1>
              <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-600 dark:text-slate-300">
                {canManage ? 'Cadastre propostas para avaliação da Câmara.' : 'Analise as propostas publicadas e selecione as que seguirão para uma futura emenda.'}
              </p>
            </div>
          </div>

          {canManage && (
            <div className="flex flex-col items-start gap-2 sm:items-end">
              <Button variant="primary" onClick={() => navigate('/dashboard/pre-projetos/novo')} disabled={!!bloqueioJanela} title={bloqueioJanela || undefined}>
                + Novo pré-projeto
              </Button>
              {bloqueioJanela && (
                <p className="max-w-xs text-xs font-medium text-warning-text sm:text-right">
                  Fora do prazo. {bloqueioJanela}
                </p>
              )}
            </div>
          )}
        </div>

        <div className="mt-6 grid grid-cols-1 gap-px overflow-hidden rounded-2xl border border-slate-200 bg-slate-200 sm:grid-cols-3 dark:border-slate-700 dark:bg-slate-700">
          {summaryCards.map((card) => (
            <div key={card.label} className={`${isSaas ? 'bg-white/90 dark:bg-slate-800/80' : 'bg-white dark:bg-slate-800'} px-4 py-3`}>
              <div className="flex items-center gap-2">
                <span className={`h-2 w-2 rounded-full ${card.dot}`} />
                <p className="truncate text-[0.68rem] font-semibold uppercase tracking-[0.1em] text-slate-500 dark:text-slate-400">{card.label}</p>
              </div>
              <p className="mt-2 text-2xl font-bold tabular-nums text-slate-950 dark:text-slate-100">{card.value}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="flex flex-col gap-3 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-700 dark:bg-slate-800/70 sm:flex-row">
        <input value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }} placeholder="Buscar por título, objeto, instituição ou área" aria-label="Buscar por título, objeto, instituição ou área" title="Buscar por título, objeto, instituição ou área"
          className={`${FIELD_BASE_CLASS} min-w-0 flex-1`} />
        <PreProjetoFiltros items={items} value={filtros} onChange={(value) => { setFiltros(value); setPage(1); }} />
        <select value={etapa} onChange={(e) => { setEtapa(e.target.value); setPage(1); }}
          className={FIELD_BASE_CLASS}>
          <option value="">Todas as etapas</option>
          {Object.entries(ETAPA_LABEL).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
        </select>
        <label className="flex items-center gap-2 whitespace-nowrap text-sm text-slate-600 dark:text-slate-300">
          <input
            type="checkbox"
            checked={somenteBloqueadas}
            onChange={(e) => { setSomenteBloqueadas(e.target.checked); setPage(1); }}
            className="h-4 w-4 rounded border-line-strong text-teal-600 focus:ring-teal-500 dark:text-teal-300"
          />
          Somente instituições bloqueadas
        </label>
      </section>

      {loading ? (
        <section className="rounded-2xl border border-slate-200 bg-white p-12 text-center text-sm text-slate-500 shadow-sm dark:border-slate-700 dark:bg-slate-800/70 dark:text-slate-400">
          Carregando pré-projetos...
        </section>
      ) : error ? (
        <section className="rounded-2xl border border-red-200 bg-red-50 p-8 text-center shadow-sm dark:border-red-800 dark:bg-red-900/20">
          <p className="font-semibold text-red-800 dark:text-red-300">Erro ao carregar pré-projetos</p>
          <p className="mt-2 text-sm text-red-700 dark:text-red-400">{error}</p>
        </section>
      ) : visible.length === 0 ? (
        <section className="rounded-2xl border border-slate-200 bg-white p-12 text-center text-sm text-slate-500 shadow-sm dark:border-slate-700 dark:bg-slate-800/70 dark:text-slate-400">
          <p className="text-lg font-semibold text-slate-900 dark:text-slate-100">Nenhum pré-projeto encontrado.</p>
        </section>
      ) : isSaas ? (
        <section className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {visible.map((item) => (
            <article key={item.id} className="flex flex-col rounded-2xl border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-700 dark:bg-slate-900">
              <div className="flex items-start justify-between gap-3">
                <span className="rounded-full bg-teal-50 px-2.5 py-1 text-xs font-semibold text-teal-700 dark:bg-teal-950 dark:text-teal-300">{ETAPA_LABEL[item.etapa]}</span>
                <span className="text-xs text-fg-muted">Exercício: {item.exercicio}</span>
              </div>
              {item.instituicaoBloqueada && (
                <span className="mt-2 w-fit rounded-full bg-red-50 px-2.5 py-1 text-xs font-semibold text-red-700 dark:bg-red-950 dark:text-red-300">
                  Instituição bloqueada
                </span>
              )}
              <p className="mt-4 font-mono text-xs font-semibold text-fg-muted">{item.codigo}</p>
              <h2 className="mt-1 text-base font-semibold text-slate-900 dark:text-white">{item.titulo}</h2>
              <p className="mt-1 text-xs text-fg-muted">{item.instituicaoNome}</p>
              <p className="mt-3 line-clamp-2 text-sm text-slate-600 dark:text-slate-300">{item.objeto}</p>
              <div className="mt-5 grid grid-cols-2 gap-3 border-t border-slate-100 pt-4 text-sm dark:border-slate-800">
                <div><p className="text-xs text-fg-muted">Solicitado</p><p className="font-semibold">{money(item.valorSolicitado)}</p></div>
                <div><p className="text-xs text-fg-muted">Restante</p><p className="font-semibold text-teal-700 dark:text-teal-300">{money(item.valorRestante)}</p></div>
              </div>
              <button onClick={() => navigate(`/dashboard/pre-projetos/${item.id}`)} className="mt-5 text-left text-sm font-semibold text-teal-700 hover:text-teal-800 dark:text-teal-300">
                Ver pré-projeto →
              </button>
            </article>
          ))}
        </section>
      ) : (
        <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-900">
          <div className="overflow-x-auto">
            <Table>
              <Thead>
                <Tr><Th>Instituição</Th><Th>Objeto</Th><Th>Etapa</Th><Th>Área</Th><Th align="right">Valor restante</Th><Th>Abrir</Th></Tr>
              </Thead>
              <Tbody>
                {visible.map((item) => (
                  <Tr key={item.id}>
                    <Td>
                      <p className="font-medium text-slate-900 dark:text-white">{item.instituicaoNome}</p>
                      <p className="text-xs text-fg-muted">Exercício: {item.exercicio}</p>
                      {item.instituicaoBloqueada && (
                        <span className="mt-1 inline-block rounded-full bg-red-50 px-2 py-0.5 text-xs font-semibold text-red-700 dark:bg-red-950 dark:text-red-300">
                          Instituição bloqueada
                        </span>
                      )}
                    </Td>
                    <Td className="max-w-md"><p className="font-mono text-xs font-semibold text-fg-muted">{item.codigo}</p><p className="font-medium text-slate-800 dark:text-slate-100">{item.titulo}</p><p className="line-clamp-2 text-xs text-fg-muted">{item.objeto}</p></Td>
                    <Td>{ETAPA_LABEL[item.etapa]}</Td>
                    <Td>{item.areaAtuacao || '—'}</Td>
                    <Td align="right" className="font-semibold">{money(item.valorRestante)}</Td>
                    <Td><button onClick={() => navigate(`/dashboard/pre-projetos/${item.id}`)} className="font-semibold text-link">Abrir</button></Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </div>
        </section>
      )}

      {!loading && !error && (
        <DashboardPagination page={page} pageSize={pageSize} totalItems={filtered.length}
          onPageChange={setPage} onPageSizeChange={(value) => { setPageSize(value); setPage(1); }} itemLabel="pré-projetos" />
      )}
    </div>
  );
};

export default PreProjetosPage;
